import Vue from 'vue'
import { api } from 'src/plugins/axios'
import { addressKey, isCheckable } from 'src/utils/addressVerification'

/**
 * Google address verification results, cached by address.
 *
 * Shared by every chip on the page, so the same address typed for two
 * students (or synced into a second session) is checked once.
 *
 * Informational only: nothing is held back because of a result.
 *
 * Each entry is { status, suggestion, token }, where status is one of:
 *   checking     request in flight
 *   verified     Google confirmed it as entered
 *   fix          Google confirmed only its corrected version (`suggestion`)
 *   unverified   Google could not confirm it
 *   unavailable  Google could not be reached; worth a retry
 *
 * `token` is the server's proof of the result, sent back with the class save
 * so the save can record it without asking Google again.
 *
 * Nothing is checked while address verification is turned off in Settings.
 */
// In-flight requests by address key, so a caller that needs the final result
// (the class save) can wait on a check a blur already started.
const inflight = {}

// Address key each saved address row has had its result stored for, by
// ADDRESS_ID. A result reused from the cache for a row not in here (another
// student with the same address) is still sent to be stored on that row.
const recorded = {}

export default {
  namespaced: true,
  state: {
    checks: {},
  },
  getters: {
    checkFor: state => address => state.checks[addressKey(address)] || null,
  },
  mutations: {
    setCheck (state, { key, check }) {
      Vue.set(state.checks, key, check)
    },
  },
  actions: {
    /**
     * Mark addresses whose result the server already has, without asking
     * Google again. A seeded `fix` has no suggestion; its chip fetches one
     * when clicked.
     *
     * @param {Array<{address, status, addressId}>} entries
     */
    seed ({ commit }, { entries }) {
      entries.forEach(({ address, status, addressId }) => {
        const key = addressKey(address)
        commit('setCheck', { key, check: { status, suggestion: null, token: null } })
        if (addressId) recorded[addressId] = key
      })
    },

    /**
     * Check an address with Google, unless it already has a settled result.
     * `force` re-asks, for a retry or a "check again". `addressId` is the
     * saved address row, if any; the server stores the result on it.
     *
     * @returns the cache entry
     */
    async check ({ state, commit, rootGetters }, { address, addressId = null, force = false }) {
      if (!rootGetters['settings/addressVerificationEnabled']) return null
      if (!isCheckable(address)) return null

      const key = addressKey(address)
      if (inflight[key]) return inflight[key]

      // A settled result stands unless asked again. An outage is never
      // settled: the next blur or save tries again.
      const existing = state.checks[key]
      if (existing && !force && existing.status !== 'unavailable') {
        if (!addressId || recorded[addressId] === key) return existing
        // Settled, but not stored on this row yet. With the earlier check's
        // token the server stores it without asking Google; without one
        // (a result seeded from another row) the address is checked again.
        if (existing.token) {
          store(addressId, key, address, existing.token)
          return existing
        }
      }

      inflight[key] = request({ commit }, key, address, addressId)
      try {
        return await inflight[key]
      } finally {
        delete inflight[key]
      }
    },
  },
}

async function request ({ commit }, key, address, addressId) {
  commit('setCheck', { key, check: { status: 'checking', suggestion: null, token: null } })

  let check
  try {
    const { data } = await api.post('/address/validate', Object.assign({}, address, { addressId }))
    if (!data || !data.success) throw new Error((data && data.message) || 'No response')

    const { status, standardized, verificationToken, correctionToken } = data.payload

    // Turned off in Settings since the page loaded
    if (status === 'disabled') {
      commit('setCheck', { key, check: null })
      return null
    }

    check = {
      status,
      suggestion: status === 'fix' ? standardized : null,
      token: verificationToken || null,
    }

    // Google's version is what the row holds once the user accepts it, saved
    // as verified and counted as fixed.
    if (status === 'fix' && standardized && correctionToken) {
      commit('setCheck', {
        key: addressKey(standardized),
        check: { status: 'verified', suggestion: null, token: correctionToken },
      })
    }
  } catch (err) {
    console.log('address check failed', err)
    check = { status: 'unavailable', suggestion: null, token: null }
  }

  if (addressId && check && check.status !== 'unavailable') recorded[addressId] = key
  commit('setCheck', { key, check })
  return check
}

/**
 * Store a result the page already has on a saved address row.
 */
async function store (addressId, key, address, token) {
  recorded[addressId] = key
  try {
    const { data } = await api.post('/address/validate', Object.assign({}, address, { addressId, verificationToken: token }))
    if (!data || !data.success) throw new Error((data && data.message) || 'No response')
  } catch (err) {
    console.log('address result not stored', err)
    delete recorded[addressId]
  }
}
